import * as React from 'react';
import { Paper, PromptConfirmation } from 'ui';

import { withPrompt, PromptProps } from 'app/components';
import { STATE } from 'app/constants';
import { DateFormatters } from 'app/infrastructure/data/types';
import { createFormatters } from 'app/infrastructure/data/date';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { safeNumericString } from 'app/infrastructure/data/sanitize';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { hasTranslationsLoaded, translationChanged } from 'app/infrastructure/translate/selectors';
import { routeConstants } from 'app/routes/route-constants';
import * as navigate from 'app/area/shared/navigation';

import { getAdminMessageAccess } from '../common/get-admin-message-access';
import { administrationMessagesTranslation } from '../constants';
import { AdministrationMessagesState } from '../types-store';

import { MessageFormRenderer } from './components/message-form-renderer';
import { hasChanges } from './utils';
import { actionCreators } from './actions';
import { scrollToTop } from 'app/infrastructure/platform/routing';

export type MessagesFormProps = DefaultContainerProps
  & PromptProps
  & AdministrationMessagesState
  & typeof actionCreators;

class MessagesFormComponent extends React.PureComponent<MessagesFormProps> {
  public static statePath: string = STATE.administrationMessages;

  private formatters: DateFormatters;

  public constructor(props: MessagesFormProps) {
    super(props);

    this.formatters = createFormatters(props.sharedState.locale);
  }

  public componentDidMount() {
    scrollToTop();
    this.props.loadLanguageOptions();

    if (hasTranslationsLoaded(this.props.translation)) {
      const messageId = safeNumericString(this.props.params.id);
      this.initForm(messageId);
    }
  }

  public componentDidUpdate(prevProps: MessagesFormProps) {
    const messageId = safeNumericString(this.props.params.id);
    const prevMessageId = safeNumericString(prevProps.params.id);
    if (messageId !== prevMessageId ||
      translationChanged(this.props.translation, prevProps.translation)) {
      this.initForm(messageId);
    }
  }

  private initForm(messageId: number | '') {
    const { sharedState, loadMessage, clearForm, displayError, translation } = this.props;

    const adminMessageAccess = getAdminMessageAccess(sharedState);
    if (!adminMessageAccess.read) {
      displayError(translation.no_access_to_message_administration);
      navigate.toStartupView(true);
      return;
    }

    if (isDefinedIncludingZero(messageId)) {
      clearForm();
      loadMessage(Number(messageId));
    } else {
      if (adminMessageAccess.edit) {
        clearForm();
      } else {
        navigate.replace({ pathname: routeConstants.administrationMessagesList });
      }
    }
  }

  public render() {

    const { sharedState, form, translation,
      deleteMessage, saveMessage,
      onSubjectChange, onMessageChange,
      copySubjectToAllTranslations, copyMessageToAllTranslations,
      changeActive,
      updateValidation, displayError
    } = this.props;

    const adminMessageAccess = getAdminMessageAccess(sharedState);

    const mainLanguage = this.props.sharedState && this.props.sharedState.language
      ? this.props.sharedState.language.value
      : undefined;

    return <div className="entity-view">
      <Paper componentId="code-base-paper" className={undefined}>
        {!!this.props.shouldDisplayModal
          ? <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel}
          />
          : ''}
        <MessageFormRenderer
          formState={form}
          mainLanguage={mainLanguage}
          canEdit={adminMessageAccess.edit}
          canDelete={adminMessageAccess.delete}
          saveMessage={saveMessage}
          deleteMessage={deleteMessage}
          onSubjectChange={onSubjectChange}
          onMessageChange={onMessageChange}
          copySubjectToAllTranslations={copySubjectToAllTranslations}
          copyMessageToAllTranslations={copyMessageToAllTranslations}
          changeActive={changeActive}
          updateValidation={updateValidation}
          displayError={displayError}
          translation={translation}
          formatters={this.formatters}
        />
      </Paper>
    </div>;
  }
}

const selectors = {
  shouldPrompt: ({ form: { message, originalMessage } }: AdministrationMessagesState) =>
    hasChanges(message, originalMessage)
};

const container = containerFactory(
  withPrompt(MessagesFormComponent), actionCreators, selectors, administrationMessagesTranslation);

export { container as AdministrationMessageForm };
